import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import moment from 'moment';
import {formatNumber, loader} from 'helpers/generic';
import { getCommunity } from 'helpers/community';
import { svgElementToB64Png } from 'helpers/images';
import Incomes from './Incomes';
import Expenses from './Expenses';
import Morosity from './Morosity';
import IncomesPending from './IncomesPending';

const ShortStyled = styled.div`
    padding: 20px;

    h1 {
        text-align: center;
        font-size: 25px;
        display: block;
        margin-bottom: 20px;
    }

    h2 {
        font-size: 22px;

        b {
            font-weight: 600;
        }
    }

    table#movements {
        font-size: 13px;

        td {
            &:nth-child(2),
            &:nth-child(3) {
                width: 50px;
                text-align: right;
                white-space: nowrap;
            }
        }

        tfoot {
            font-weight: 600;
        }
    }
`;

let axiosCancelToken = null;

export default function Short(props) {
	const year = props.year;
	const setExportBtn = props.setExportBtn;

    const [loading, setLoading] = useState(false);
    const [exportChartsFix, setExportChartsFix] = useState(null);
    const [data, setData] = useState(null);
    const [cacheInfo, setCacheInfo] = useState(null);
    const [jobInfo, setJobInfo] = useState(null);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState(null);

    // Estado para detectar si hay una nueva versión del balance
    const [hasNewVersion, setHasNewVersion] = useState(false);
    const lastGeneratedAtRef = useRef(null); // Usamos ref persistente

    // Modificado: fetchCache acepta un flag para no mostrar loading (por ejemplo, en polling)
    const fetchCache = useCallback(async (showLoading = true) => {
        if (showLoading) setLoading(true);
        setError(null);
        setData(null);
        setCacheInfo(null);
        setJobInfo(null);
        try {
            const res = await axios.get('/api/manager/balances/cached', {
                params: {
                    community_id: getCommunity()?.id,
                    type: 'short',
                    financial_year: year
                }
            });
            setCacheInfo(res.data.cache);
            setJobInfo(res.data.job);
            setData(res.data.cache?.data || null);
            // Actualizamos la ref con el generated_at actual
            lastGeneratedAtRef.current = res.data.cache?.generated_at || null;
        } catch (e) {
            setError('No se pudo obtener el balance.');
        }
        if (showLoading) setLoading(false);
    }, [year]);

    const handleRequestJob = async () => {
        setRefreshing(true);
        setError(null);
        try {
            await axios.post('/api/manager/balances/request-job', {
                community_id: getCommunity()?.id,
                type: 'short',
                financial_year: year
            });
            fetchCache();
        } catch (e) {
            if (e.response && e.response.status === 409) {
                setError('Ya hay un balance en proceso para este año.');
            } else {
                setError('Error al solicitar la generación del balance.');
            }
        }
        setRefreshing(false);
    };

    // Controla si ya se hizo el fetch inicial para evitar doble request-job
    const [fetched, setFetched] = useState(false);
    const pollingRef = useRef(false);

    // Fetch inicial sólo una vez (ignora StrictMode doble render)
    useEffect(() => {
        let ignore = false;
        if (!fetched && !ignore) {
            setFetched(true);
            fetchCache();
        }
        return () => { ignore = true; };
    }, [fetched, fetchCache]);

    // Polling cada 5s si hay un job en proceso, pero sólo para detectar nueva versión
    useEffect(() => {
        let interval = null;
        if (jobInfo && (jobInfo.status === 'pending' || jobInfo.status === 'processing')) {
            pollingRef.current = true;
            interval = setInterval(async () => {
                try {
                    const res = await axios.get('/api/manager/balances/cached', {
                        params: {
                            community_id: getCommunity()?.id,
                            type: 'short',
                            financial_year: year
                        }
                    });
                    
                    // Si el job cambió a done y hay cache más reciente
                    if (res.data.job?.status === 'done' && 
                        res.data.cache?.generated_at && 
                        res.data.cache.generated_at !== lastGeneratedAtRef.current) {
                        setHasNewVersion(true);
                        setJobInfo(res.data.job);
                        setCacheInfo(res.data.cache);
                    }
                } catch (e) {
                    console.error('Error en polling:', e);
                }
            }, 5000);
        } else {
            pollingRef.current = false;
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [jobInfo, cacheInfo, year]);

    // Handler para recargar los datos manualmente
    const handleReload = async () => {
        setHasNewVersion(false);
        await fetchCache();
    }

    // Si hay un job en proceso, ocultar el aviso de nueva versión
    useEffect(() => {
        if (jobInfo && (jobInfo.status === 'pending' || jobInfo.status === 'processing')) {
            setHasNewVersion(false);
        }
    }, [jobInfo]);

    useEffect(() => {
        if (data !== null && !loading) {
            setExportBtn(<ExportBtn year={year} setExportChartsFix={setExportChartsFix} colors={data?.colors} />);
        } else {
            setExportBtn(null);
        }
    }, [year, data?.colors, loading]);

    useEffect(() => {
        axiosCancelToken = axios.CancelToken.source();

        return function cleanup() {
            axiosCancelToken.cancel();
        }
    }, [year]);    

    // Calc totals
    let initial_balance_total = parseFloat(data?.initial_balance?.receipts_pending ?? 0) + parseFloat(data?.initial_balance?.providers_pending ?? 0);
    // let final_balance_total = parseFloat(data?.final_balance?.receipts_pending ?? 0) + parseFloat(data?.final_balance?.providers_pending ?? 0);

    return (
        <>
            {loading && <div style={{textAlign: 'center'}}>{loader}</div>}

            {error ? (
                <div className="alert alert-danger m-3" role="alert">{error}</div>
            ) :
            (!hasNewVersion && !cacheInfo && !loading && !(jobInfo && (jobInfo.status === 'pending' || jobInfo.status === 'processing'))) && (
                <div className="alert alert-warning m-3" role="alert">No hay balance en cache para este año.</div>
            )}

            {cacheInfo && cacheInfo.generated_at && (
                <div className="alert alert-info m-3" role="alert">
                    Balance generado el: {new Date(cacheInfo.generated_at).toLocaleString()}
                </div>
            )}

            {/* Mostrar aviso de nueva versión solo si hay una nueva versión Y no hay job en proceso */}
            {hasNewVersion && (!jobInfo || (jobInfo.status !== 'pending' && jobInfo.status !== 'processing')) && (
                <div className="alert alert-success m-3 d-flex align-items-center justify-content-between" role="alert">
                    <span>Hay una nueva versión del balance disponible.</span>
                    <button className="btn btn-sm btn-success text-white ms-3" onClick={handleReload}>Recargar datos</button>
                </div>
            )}

            {/* Mostrar botón de generar solo si NO hay job en proceso y NO hay nueva versión pendiente de recarga */}
            {!hasNewVersion && (!jobInfo || (jobInfo.status !== 'pending' && jobInfo.status !== 'processing')) && (
                <button className="btn btn-primary text-white m-3 my-0" onClick={handleRequestJob} disabled={refreshing}>
                    {refreshing ? 'Solicitando...' : 'Generar/Recalcular balance'}
                </button>
            )}

            {/* Mostrar aviso de proceso solo si NO hay nueva versión pendiente de recarga y hay job en proceso */}
            {!hasNewVersion && jobInfo && (jobInfo.status === 'pending' || jobInfo.status === 'processing') && (
                <div className="alert alert-warning m-3" role="alert">Hay un balance en proceso para este año. Por favor, espera a que termine.</div>
            )}

            {(data !== null && !loading) && 
                <ShortStyled className="row">
                    <div className="col-md-12">
                        <h1 className="title">BALANCE RESUMIDO EJERCICIO FISCAL {year}</h1>
                    </div>

                    <div className="col-md-12 mb-3">
                        <table id="movements" className="table table-sm table-bordered">
                            <thead>
                                <tr>
                                    <th>SALDO INICIAL A { moment(year + '-01-01').format('DD-MM-YYYY') }</th>
                                    <th></th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {data?.initial_balance?.treasuries?.map((el, idx) => {
                                    initial_balance_total += parseFloat(el.amount ?? 0);

                                    return (
                                        <tr key={idx}>
                                            <td>{el.name}</td>
                                            <td></td>
                                            <td>{formatNumber(el.amount ?? 0, 2, true)} €</td>
                                        </tr>
                                    );
                                })}
                                <tr>
                                    <td>Recibos pendientes de cobro</td>
                                    <td></td>
                                    <td>{formatNumber(data?.initial_balance?.receipts_pending ?? 0, 2, true)} €</td>
                                </tr>
                                <tr>
                                    <td>Facturas pendientes de pago</td>
                                    <td></td>
                                    <td>{formatNumber(data?.initial_balance?.providers_pending ?? 0, 2, true)} €</td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td></td>
                                    <td>TOTAL SALDO INICIAL</td>
                                    <td>{formatNumber(initial_balance_total, 2, true)} €</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                    
                    <div className="col-md-12">
                        <div className="row align-items-center">
                            <Incomes 
                                data={data}
                                exportChartFix={exportChartsFix}
                            />
                        </div>
                    </div>

                    <div className="col-md-12">
                        <div className="row align-items-center">
                            <Morosity 
                                data={data}
                                exportChartFix={exportChartsFix}
                            />
                        </div>
                    </div>
                    
                    <div className="col-md-12">
                        <div className="row align-items-start">
                            <Expenses 
                                data={data} 
                                exportChartFix={exportChartsFix}
                            />
                        </div>
                    </div>

                    {/* <div className="col-md-12">
                        <div className="row align-items-center">
                            <IncomesPending 
                                data={data}
                                exportChartFix={exportChartsFix}
                            />
                        </div>
                    </div> */}

                    <div className="col-md-12 mt-5 mb-3">
                        <table id="movements" className="table table-sm table-bordered">
                            <thead>
                                <tr>
                                    <th>
                                        SALDO FINAL A&nbsp;
                                        {(() => {
                                            let date = null;
                                            if ( parseInt(year) === parseInt(moment().format('YYYY')) ) {
                                                date = moment().format('YYYY-MM-DD');
                                            } else {
                                                date = year + '-12-31';
                                            }
                                            return moment(date).format('DD-MM-YYYY');
                                        })()}
                                    </th>
                                    <th></th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {data?.final_balance?.treasuries?.map((el, idx) => {
                                    return (
                                        <tr key={idx}>
                                            <td>{el.name}</td>
                                            <td></td>
                                            <td>{formatNumber(el.amount ?? 0, 2, true)} €</td>
                                        </tr>
                                    );
                                })}
                                {/* <tr>
                                    <td>Recibos pendientes de cobro</td>
                                    <td></td>
                                    <td>{formatNumber(data?.final_balance?.receipts_pending ?? 0, 2, true)} €</td>
                                </tr> */}
                                <tr>
                                    <td>Saldo negativo de recibos</td>
                                    <td></td>
                                    <td>{formatNumber(data?.final_balance?.debt_pending_payment ?? 0, 2, true)} €</td>
                                </tr>
                                <tr>
                                    <td>Facturas pendientes de pago</td>
                                    <td></td>
                                    <td>{formatNumber(data?.final_balance?.providers_pending ?? 0, 2, true)} €</td>
                                </tr>
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td></td>
                                    <td>TOTAL SALDO CONTABLE</td>
                                    <td>
                                        {(() => {
                                            let balance = 0;

                                            data?.final_balance?.treasuries?.map((el, idx) => {
                                                balance += parseFloat(el.amount ?? 0);
                                            })

                                            // balance += data?.final_balance?.receipts_pending ?? 0;
                                            balance += data?.final_balance?.debt_pending_payment ?? 0;

                                            balance += data?.final_balance?.providers_pending ?? 0;

                                            return formatNumber(balance, 2, true);
                                        })()}
                                        €
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    {/* <div className="col-md-12 mt-3 text-end">
                        <h2 className="mb-0 d-inline-block bg-light px-2">
                            SALDO: 
                            
                            €
                        </h2>     
                    </div> */}
                </ShortStyled>
            }
        </>
	);
}

const ExportBtn = (props) => {
    const {year, setExportChartsFix, colors} = props;
    
    const exportClick = async () => {
        // Fix to disable the charts selection
		setExportChartsFix(Math.random());
		await new Promise(resolve => setTimeout(resolve, 1000));

		// Check if exists charts to send to backend
		let charts = document.querySelectorAll('[data-piecharwrapper]');
		let chartsImg = [];
		if ( charts.length > 0 ) {
			for await (let chart of charts) {
				let svg = chart.querySelector('svg');
				let b64png = await svgElementToB64Png(svg);

				chartsImg.push({
					id: chart.getAttribute('data-piecharwrapper'),
					b64png: b64png
				});
			}
		}

		// Disable fix
		setExportChartsFix(null);

		await axios.post('/api/manager/balances/export/short', {
			community_id: getCommunity()?.id,
			financial_year: year,
			charts_images: chartsImg,
			colors: colors
		}, {
			responseType: 'blob',
			cancelToken: axiosCancelToken.token
		}).then((response) => {
			let fileName = response.headers.file_name;

			const url = window.URL.createObjectURL(new Blob([response.data]));
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', fileName);
			document.body.appendChild(link);
			link.click();
			link.parentElement.removeChild(link);
	  	}).catch((error) => {
			if ( axios.isCancel(error) ) return;
		});
    }

    return (
        <button className="btn btn-sm btn-light ms-2" onClick={exportClick}>Exportar</button>	
    );
}